/**
 * Suppliers Hook
 * Handles fetching suppliers with proper error handling
 */

import { useLogger } from '@/lib/logger';
import { useOrganization } from '@/contexts/OrganizationContext';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import type { Supplier } from '../types';
import { useEffect } from 'react';

interface UseSuppliersOptions {
  includeInactive?: boolean;
  /** Default true. False makes no call (a caller without the permission the list needs). */
  enabled?: boolean;
}

export function useSuppliers(options: UseSuppliersOptions = {}) {
  const logger = useLogger();
  const { organization } = useOrganization();
  const includeInactive = options.includeInactive ?? false;
  const enabled = options.enabled ?? true;

  const query = trpc.suppliers.list.useQuery(
    { includeInactive },
    {
      enabled: enabled && !!organization?.id,
      retry: 2,
      retryDelay: 1000,
      staleTime: 30_000,
    }
  );

  useEffect(() => {
    if (query.data) {
      logger.info('Suppliers fetched successfully', {
        operation: 'fetch_suppliers',
        organizationId: organization?.id,
        count: query.data.length,
        includeInactive,
      });
    }

    if (query.error) {
      Sentry.captureException(query.error, {
        tags: {
          feature: 'suppliers',
          operation: 'fetch_suppliers',
        },
      });

      logger.error('Failed to fetch suppliers', {
        operation: 'fetch_suppliers',
        error: query.error.message,
        includeInactive,
      });
    }
  }, [query.data, query.error, logger, organization?.id, includeInactive]);

  return {
    suppliers: (query.data ?? []) as Supplier[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
